import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRxCollection } from 'rxdb/plugins/react';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    ParcelDocType,
    ProjectDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    TranslationDocType
} from '@ast-data/shared';
import { useToast } from '../../../components/toastContext';
import { downloadBlob } from '../../../utils/downloadFile';
import { confirmUnavailableMedia } from '../pdf/confirmUnavailableMedia';
import { useConfirm } from '../../../components/confirmContext';
import { createDeliverableTexts } from '../../translation/deliverableTexts';
import { confirmTranslationGaps } from '../../translation/confirmTranslationGaps';
import {
    collectFindingExportData,
    findingPdfFileName,
    type ExportCollections
} from './exportData';
import {
    countDescriptions,
    PARTS_THRESHOLD,
    type RenderProgress
} from './partsPolicy';
import { isEmptyScope, type ExportScope } from '../pdf/exportScope';
import { usePersonLabels } from '../../users/personLabels';
import { recordsMeasureParts } from '../../projects/projectTitle';

/** A pending size warning: what it is about, and how to answer it. */
export interface PdfExportConfirm {
    findings: number;
    descriptions: number;
    resolve: (approved: boolean) => void;
}

/**
 * Export the finding descriptions of a project to a single PDF and download it.
 * `exportPdf(projectId, findingIds)` renders the given findings (or `'all'`);
 * `busyId` is the project id currently rendering (null when idle), so a grid can
 * show a per-row spinner. The family (for shared parcels) is resolved from the
 * project, so this works for any project id. i18n comes from the app's `useIntl`,
 * passed into the react-pdf document (which renders outside the IntlProvider).
 */
export function useFindingPdfExport() {
    const intl = useIntl();
    const { notify } = useToast();
    const { confirm: askUser } = useConfirm();
    const [busyId, setBusyId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // Only set while rendering in parts — a small export finishes before any
    // progress could be shown.
    const [progress, setProgress] = useState<RenderProgress | null>(null);
    // Set while the size warning is up; its `resolve` continues or abandons
    // the export the user triggered.
    const [confirm, setConfirm] = useState<PdfExportConfirm | null>(null);

    // The sheets name people by their accounts, as the screens do.
    const personLabels = usePersonLabels();
    const projects = useRxCollection<ProjectDocType>('projects');
    const findings = useRxCollection<FindingDocType>('findings');
    const planums = useRxCollection<FindingPlanumDocType>('finding_planums');
    const profiles = useRxCollection<FindingProfileDocType>('finding_profiles');
    const parcels = useRxCollection<ParcelDocType>('parcels');
    const media = useRxCollection<MediaDocType>('media');
    const mediaLinks = useRxCollection<MediaLinkDocType>('media_links');
    const mediaGroups = useRxCollection<MediaGroupDocType>('media_groups');
    const translations = useRxCollection<TranslationDocType>('translations');

    const exportPdf = useCallback(
        async (scope: ExportScope, busyKey?: string) => {
            if (
                !projects ||
                !findings ||
                !planums ||
                !profiles ||
                !parcels ||
                !media ||
                !mediaLinks ||
                !mediaGroups
            ) {
                return;
            }
            if (isEmptyScope(scope)) {
                return;
            }
            setBusyId(busyKey ?? null);
            setBusy(true);
            try {
                const collections: ExportCollections = {
                    projects,
                    findings,
                    planums,
                    profiles,
                    parcels,
                    media,
                    mediaLinks,
                    mediaGroups
                };
                const deliverable = createDeliverableTexts(
                    translations,
                    projects
                );
                const data = await collectFindingExportData({
                    collections,
                    scope,
                    labelOf: personLabels.labelOf,
                    deliverable
                });
                /**
                 * Two different nothings, and they used to share one message.
                 * Befunde are dropped when their Maßnahme is not in the local
                 * database — deleted (its Befunde outlive it and stay reachable
                 * by URL), or simply not synced to this device yet. The sheet's
                 * whole head comes from the project, so there is genuinely
                 * nothing to print either way — but "keine Befunde" sent the
                 * reader looking for the wrong thing.
                 */
                if (
                    data.findings.length === 0 &&
                    data.missingProjectCount > 0
                ) {
                    notify(
                        intl.formatMessage({ id: 'export.missingProject' }),
                        'warning'
                    );
                    return;
                }
                if (data.findings.length === 0) {
                    notify(
                        intl.formatMessage({ id: 'findingPdf.empty' }),
                        'info'
                    );
                    return;
                }
                // Offline, pictures not yet on this device cannot print — ask
                // before rendering a PDF without them.
                if (
                    !(await confirmUnavailableMedia(
                        askUser,
                        intl,
                        data.findings.flatMap((entry) =>
                            entry.descriptions.flatMap(
                                (description) => description.media
                            )
                        )
                    ))
                ) {
                    return;
                }
                if (
                    !(await confirmTranslationGaps(
                        askUser,
                        intl,
                        deliverable.gaps
                    ))
                ) {
                    return;
                }
                // A whole-project documentation can run to thousands of sheets,
                // which react-pdf cannot hold in one tree — render those in
                // parts. Everything smaller keeps the plain one-pass path.
                const descriptions = countDescriptions(data);
                // Minutes of work on a good machine, longer on a weak one — ask
                // before starting, with the real numbers. Only for the big path;
                // a normal export is done before a dialog would be read.
                if (descriptions > PARTS_THRESHOLD) {
                    const approved = await new Promise<boolean>((resolve) =>
                        setConfirm({
                            findings: data.findings.length,
                            descriptions,
                            resolve
                        })
                    );
                    setConfirm(null);
                    if (!approved) {
                        return;
                    }
                }
                // The renderer arrives here, at the click — see renderPdf.
                const { renderFindingPdf } = await import('./renderPdf');
                const blob = await renderFindingPdf(
                    data,
                    intl,
                    descriptions > PARTS_THRESHOLD,
                    (progress) => setProgress(progress)
                );
                downloadBlob(
                    blob,
                    findingPdfFileName(
                        // The phases the descriptions were taken in.
                        data.findings.flatMap((entry) =>
                            recordsMeasureParts(
                                entry.project,
                                entry.descriptions.map((description) => {
                                    const doc =
                                        description.planum ??
                                        description.profile;
                                    return {
                                        recordedAt: doc?.recordedAt ?? '',
                                        phaseNumber: doc?.phaseNumber ?? ''
                                    };
                                })
                            ).map((parts) => parts.measureNumber)
                        ),
                        data.findings.map(
                            (entry) => entry.finding.findingNumber
                        )
                    )
                );
            } catch (cause) {
                notify(intl.formatMessage({ id: 'findingPdf.error' }), 'error');
                console.error('Finding PDF export failed', cause);
            } finally {
                setBusyId(null);
                setBusy(false);
                setProgress(null);
            }
        },
        [
            personLabels,
            projects,
            findings,
            planums,
            profiles,
            parcels,
            media,
            mediaLinks,
            mediaGroups,
            translations,
            intl,
            notify,
            askUser
        ]
    );

    /** Answer the size warning — true renders, false abandons the export. */
    const resolveConfirm = useCallback((approved: boolean) => {
        setConfirm((pending) => {
            pending?.resolve(approved);
            return null;
        });
    }, []);

    return { exportPdf, busyId, busy, progress, confirm, resolveConfirm };
}
